import { toast } from "react-toastify";
import React, { useState, useEffect, useRef } from 'react';
import { createDomain, getDomainById, updateDomain } from "../../service/api/domainService";
import { useParams } from 'react-router-dom';

const DomainForm = ({ domain, onClose }) => {
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const { id } = useParams();
    const [isEditing, setIsEditing] = useState(false);

    // Ajout du champ categorie dans l'état initial du formulaire
    const [domainData, setDomainData] = useState({
        name: '',
        description: '',
        email: '',
        photo: null,
        adress: '',      // Champ Adresse en français
        telephone: '',   // Champ Téléphone en français
        ville: '',       // Champ Ville en français
        categorie: '',   // Nouveau champ Catégorie
    });

    const fileInputRef = useRef(null); // Référence pour l'input de fichier

    // Exemple de liste statique de catégories ; à adapter selon votre contexte
    const categoriesOptions = [
        { value: '', label: 'Sélectionnez une catégorie' },
        { value: 'Private', label: 'Privee' },
        { value: 'Public', label: 'Publique' },
    ];

    useEffect(() => {
        // Si un ID est présent dans l'URL, on est en mode édition
        if (id) {
            setIsEditing(true);
            getDomainById(id)
                .then(data => {
                    setDomainData({
                        name: data.name,
                        description: data.description,
                        email: data.email,
                        photo: data.photo,
                        adress: data.adress || '',
                        telephone: data.telephone || '',
                        ville: data.ville || '',
                        // Prendre en compte la catégorie existante en édition
                        categorie: data.categorie || '',
                    });
                })
                .catch(err => console.error("Error fetching domain:", err));
        } else if (domain) {
            // Mode édition avec les données passées en prop
            setIsEditing(true);
            setDomainData({
                name: domain.name,
                description: domain.description,
                email: domain.email,
                photo: domain.photo,
                adress: domain.adress || '',
                telephone: domain.telephone || '',
                ville: domain.ville || '',
                categorie: domain.categorie || '',
            });
        } else {
            // Mode création, on initialise le formulaire avec des valeurs vides
            setIsEditing(false);
            setDomainData({ 
                name: '', 
                description: '', 
                email: '', 
                photo: null, 
                adress: '', 
                telephone: '', 
                ville: '',
                categorie: '', 
            });
        }
    }, [id, domain]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSubmitError(null);

        let newErrors = {};

        // Validation des champs obligatoires
        if (!domainData.name) {
            newErrors.name = 'Le nom est requis.';
        }
        if (!domainData.description) {
            newErrors.description = 'La description est requise.';
        }
        if (!domainData.email) {
            newErrors.email = 'L\'email est requis.';
        } else if (!isValidEmail(domainData.email)) {
            newErrors.email = 'L\'email est invalide.';
        }
        if (!domainData.adress) {
            newErrors.adress = 'L\'adresse est requise.';
        }
        if (!domainData.telephone) {
            newErrors.telephone = 'Le téléphone est requis.';
        } else if (!isValidPhone(domainData.telephone)) {
            newErrors.telephone = 'Le téléphone est invalide.';
        }
        if (!domainData.ville) {
            newErrors.ville = 'La ville est requise.';
        }
        // Validation du champ categorie (doit être sélectionné)
        if (!domainData.categorie) {
            newErrors.categorie = 'La catégorie est requise.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Préparation des données du formulaire (utilisation de FormData pour le traitement d'image)
        const formData = new FormData();
        formData.append('name', domainData.name);
        formData.append('description', domainData.description);
        formData.append('email', domainData.email);
        formData.append('adress', domainData.adress); // Correction ici
        formData.append('telephone', domainData.telephone);
        formData.append('ville', domainData.ville);
        formData.append('categorie', domainData.categorie); 

        if (domainData.photo) {
            if (typeof domainData.photo === 'object') {
                formData.append('photo', domainData.photo);
            }
        }

        try {
            if (!isEditing) {
                await createDomain(formData);
                setDomainData({ 
                    name: '', 
                    description: '', 
                    email: '', 
                    photo: null, 
                    adress: '', 
                    telephone: '', 
                    ville: '',
                    categorie: '', 
                });
                toast.success(`Domaine ${domainData.name} créé avec succès !`);
                onClose();
            } else {
                await updateDomain(id || domain.id, formData);
                setDomainData({ 
                    name: '', 
                    description: '', 
                    email: '', 
                    photo: null, 
                    adress: '', 
                    telephone: '', 
                    ville: '',
                    categorie: '', 
                });
                toast.success(`Domaine ${domainData.name} modifié avec succès !`);
                onClose();
            }
        } catch (err) {
            setSubmitError(err.message);
        }
    };

    // Gestion des changements des inputs
    const handleChange = (e) => {
        const { name, value } = e.target;

        setDomainData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Exemple de validation pour le champ name (en majuscules)
        if (name === 'name') {
            const uppercaseRegex = /^[A-Z\s]+$/;
            setErrors((prevErrors) => ({
                ...prevErrors,
                name: uppercaseRegex.test(value) ? '' : 'Le nom doit être en majuscules uniquement.',
            }));
        }

        // Validation instantanée pour l'email
        if (name === 'email') {
            setErrors((prevErrors) => ({
                ...prevErrors,
                email: isValidEmail(value) ? '' : 'L\'email est invalide.',
            }));
        }

        // Validation instantanée pour le téléphone
        if (name === 'telephone') {
            setErrors((prevErrors) => ({
                ...prevErrors,
                telephone: isValidPhone(value) ? '' : 'Le téléphone est invalide.',
            }));
        }
    };

    // Gestion du changement de fichier pour le logo
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            const validExtensions = ['image/jpeg', 'image/png', 'image/gif', 'image/jpg', 'image/webp'];
            const maxSize = 2 * 1024 * 1024; // 2MB

            if (!validExtensions.includes(file.type)) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    photo: 'Format d’image invalide. Seuls JPG, PNG et GIF sont autorisés.',
                }));
                return;
            }

            if (file.size > maxSize) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    photo: 'L’image ne doit pas dépasser 2MB.',
                }));
                return;
            }

            setErrors((prevErrors) => ({ ...prevErrors, photo: '' }));
            setDomainData({ ...domainData, photo: file });
        }
    };

    // Fonction pour retirer la photo sélectionnée
    const handleRemovePhoto = () => {
        setDomainData({ ...domainData, photo: null });
        if (fileInputRef.current) {
            fileInputRef.current.value = null; // Réinitialise l'input fichier
        }
    };

    // Fonctions de validation
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidPhone = (phone) => {
        // Autorise les caractères : chiffres, espaces, tirets, parenthèses, +
        const phoneRegex = /^[0-9\s+()-]+$/;
        if (!phoneRegex.test(phone)) return false;
      
        // Supprime tous les non-chiffres pour compter les chiffres
        const digitsOnly = phone.replace(/\D/g, '');
      
        // Vérifie un nombre raisonnable de chiffres (ex: 8 à 15)
        return digitsOnly.length >= 9 && digitsOnly.length <= 15;
    };
      

    return (
        <>
            <form className="needs-validation" onSubmit={handleSubmit} noValidate>
                {submitError && <div className="alert alert-danger">{submitError}</div>}

                {/* Champ Nom */}
                <div className="mb-3">
                    <label htmlFor="name" className="form-label">Nom</label>
                    <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : /^[A-Z\s]+$/.test(domainData.name) ? 'is-valid' : ''}`}
                        id="name"
                        name="name"
                        value={domainData.name}
                        onChange={handleChange}
                        required
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    {!errors.name && /^[A-Z\s]+$/.test(domainData.name) && <div className="valid-feedback">Valide.</div>}
                </div>

                {/* Champ Description */}
                <div className="mb-3">
                    <label htmlFor="description" className="form-label">Description</label>
                    <textarea
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        id="description"
                        name="description"
                        value={domainData.description}
                        onChange={handleChange}
                        required
                    />
                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                </div>

                {/* Champ Email */}
                <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : isValidEmail(domainData.email) ? 'is-valid' : ''}`}
                        id="email"
                        name="email"
                        value={domainData.email}
                        onChange={handleChange}
                        required
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    {!errors.email && isValidEmail(domainData.email) && <div className="valid-feedback">L'email est valide.</div>}
                </div>

                {/* Champ Logo */}
                <div className="mb-3">
                    <label htmlFor="logo" className="form-label">Logo</label>
                    {domainData.photo ? (
                        <div>
                            {typeof domainData.photo === 'string' ? (
                                <img
                                    src={`${import.meta.env.VITE_API_BASE_URL}${domainData.photo}`}
                                    alt="Domain Photo"
                                    style={{ maxWidth: '100px', maxHeight: '100px' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/assets/images/placeholder.png";
                                    }}
                                />
                            ) : (
                                <img
                                    src={URL.createObjectURL(domainData.photo)}
                                    alt="Selected Photo"
                                    style={{ maxWidth: '100px', maxHeight: '100px' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/assets/images/placeholder.png";
                                    }}
                                />
                            )}
                            <button type="button" className="btn btn-sm btn-danger" onClick={handleRemovePhoto}>
                                Ignorer
                            </button>
                        </div>
                    ) : (
                        <input
                            type="file"
                            className="form-control"
                            id="photo"
                            name="photo"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                        />
                    )}
                </div>

                {/* Champ Adresse */}
                <div className="mb-3">
                    <label htmlFor="adress" className="form-label">Adresse</label>
                    <input
                        type="text"
                        className={`form-control ${errors.adress ? 'is-invalid' : ''}`}
                        id="adress"
                        name="adress"
                        value={domainData.adress}
                        onChange={handleChange}
                        required
                    />
                    {errors.adress && <div className="invalid-feedback">{errors.adress}</div>}
                </div>

                {/* Champ Téléphone */}
                <div className="mb-3">
                    <label htmlFor="telephone" className="form-label">Téléphone</label>
                    <input
                        type="text"
                        className={`form-control ${errors.telephone ? 'is-invalid' : isValidPhone(domainData.telephone) ? 'is-valid' : ''}`}
                        id="telephone"
                        name="telephone"
                        value={domainData.telephone}
                        onChange={handleChange}
                        required
                    />
                    {errors.telephone && <div className="invalid-feedback">{errors.telephone}</div>}
                    {!errors.telephone && isValidPhone(domainData.telephone) && <div className="valid-feedback">Le téléphone est valide.</div>}
                </div>

                {/* Champ Ville */}
                <div className="mb-3">
                    <label htmlFor="ville" className="form-label">Ville</label>
                    <input
                        type="text"
                        className={`form-control ${errors.ville ? 'is-invalid' : ''}`}
                        id="ville"
                        name="ville"
                        value={domainData.ville}
                        onChange={handleChange}
                        required
                    />
                    {errors.ville && <div className="invalid-feedback">{errors.ville}</div>}
                </div>

                {/* Champ Catégorie (Select) */}
                <div className="mb-3">
                    <label htmlFor="categorie" className="form-label">Catégorie</label>
                    <select
                        className={`form-control ${errors.categorie ? 'is-invalid' : ''}`}
                        id="categorie"
                        name="categorie"
                        value={domainData.categorie}
                        onChange={handleChange}
                        required
                    >
                        {categoriesOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {errors.categorie && <div className="invalid-feedback">{errors.categorie}</div>}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-danger light" data-bs-dismiss="modal">
                        Close
                    </button>
                    <button type="submit" className="btn btn-primary">
                        {isEditing ? 'Mettre à jour' : 'Créer un domain'}
                    </button>
                </div>
            </form>
        </>
    );
};

export default DomainForm;
